import type { PontemWalletAdapterConfig } from "@manahippo/aptos-wallet-adapter";
import {
  AptosWalletAdapter,
  WalletProvider as AptosWalletProvider,
  MartianWalletAdapter,
  PontemWalletAdapter,
  RiseWalletAdapter,
} from "@manahippo/aptos-wallet-adapter";
import { ThemeProvider, createTheme } from "@mui/material";
import { PhantomWalletAdapter } from "@solana/wallet-adapter-phantom";
import { SnackbarProvider } from "notistack";
import { ErrorBoundary } from "react-error-boundary";
import { BrowserRouter, Navigate, Route, Routes } from "react-router-dom";

import { ErrorFallback, Footer, Gradient, ScrollToTop } from "./components";
import { cosmosConnectors } from "./connectors";
import {
  AptosSetup,
  CosmJsProvider,
  HotjarRouteInterceptor,
  SolanaProvider,
  WalletProvider,
  Web3ConnectorsProvider,
  Web3ReactProviderSupplier,
  Web3Setup,
} from "./contexts";
import { LegalPopup, Navbar } from "./features";

import { AllTokens } from "./pages/AIAnalyticsDashboard/AllTokens/AllTokens";
import { SignificantTokenTransfers } from "./pages/AIAnalyticsDashboard/SignificantTokenTransfers/SignificantTokenTransfers";
import { SingleToken } from "./pages/AIAnalyticsDashboard/SingleToken/SingleToken";

(async () => {
  const MetaMaskSDK = await import("@metamask/sdk").then((m) => m.MetaMaskSDK);

  new MetaMaskSDK({
    dappMetadata: {
      name: "Gateway",
      url: window.location.host,
    },
    useDeeplink: false,
    extensionOnly: true,
  });
})();

declare global {
  interface Window {
    pontem: PontemWalletAdapterConfig["provider"];
    xfi: any;
  }
}

const theme = createTheme({
  breakpoints: {
    values: {
      xs: 0,
      sm: 600,
      md: 900,
      lg: 1200,
      xl: 1536,
    },
  },
  palette: {
    mode: "dark",
    primary: {
      main: "#006ad9",
    },
  },
});

const aptosWallets = [
  new PontemWalletAdapter(),
  new MartianWalletAdapter(),
  new AptosWalletAdapter(),
  new RiseWalletAdapter(),
];

const solanaWallets = [new PhantomWalletAdapter()];

const App = () => {
  return (
    <ThemeProvider theme={theme}>
      <SnackbarProvider
        style={{
          backgroundColor: "#0000",
          boxShadow: "none",
          padding: 0,
        }}
        anchorOrigin={{
          vertical: "top",
          horizontal: "right",
        }}
        maxSnack={3}
      >
        <Web3ConnectorsProvider>
          <Web3ReactProviderSupplier>
            <SolanaProvider wallets={solanaWallets}>
              <CosmJsProvider connectors={cosmosConnectors}>
                <WalletProvider>
                  <AptosWalletProvider
                    wallets={aptosWallets}
                    onError={(error: Error) => {
                      console.log("Aptos provider error", error);
                    }}
                  >
                    <Web3Setup>
                      <AptosSetup>
                        <BrowserRouter>
                          <ScrollToTop />
                          <HotjarRouteInterceptor />
                          <LegalPopup />
                          <Gradient />
                          {/* It cannot be here. We will need to put it into individual app Pages components probably */}
                          <Navbar />

                          <ErrorBoundary FallbackComponent={ErrorFallback}>
                            <Routes>
                              <Route path="/" element={<AllTokens />} />

                              <Route
                                path="report/:projectId"
                                element={<SingleToken />}
                              />
                              <Route
                                path="significant-token-transfers"
                                element={<SignificantTokenTransfers />}
                              />
                              <Route
                                path="report/significant-token-transfers/:projectId"
                                element={<SignificantTokenTransfers />}
                              />
                              <Route path="/*" element={<Navigate to="/" />} />
                            </Routes>
                          </ErrorBoundary>
                          <Footer className="mt-auto" />
                        </BrowserRouter>
                      </AptosSetup>
                    </Web3Setup>
                  </AptosWalletProvider>
                </WalletProvider>
              </CosmJsProvider>
            </SolanaProvider>
          </Web3ReactProviderSupplier>
        </Web3ConnectorsProvider>
      </SnackbarProvider>
    </ThemeProvider>
  );
};

export default App;
